/**
 * The bottom tab bar: where you are, and the three other places you can be.
 *
 * Before this existed the app was a hub with spokes. Every screen ended in
 * "Done", which meant home, which meant scrolling past the gauge and the
 * tiles and the history to find the card for the next thing. Bank to wishes
 * was four steps. The bar makes it one, and the "Done" buttons are gone —
 * a sub-screen now has a back link to the tab it belongs to instead.
 *
 * Four destinations, never five, and they change with who is looking
 * (bottom-nav-limit, bottom-nav-top-level). A parent gets Home, Bank,
 * Wishes and Family; a child gets Home, their money, their list and the way
 * out. Settings-shaped screens — tiles, rules, invites, the fridge sheet —
 * live under Family rather than in the bar, because a tab is a place and a
 * rule screen is a task.
 *
 * Badges appear only where a number means "something is waiting on you":
 * unanswered asks on Home, wishes whose wait is over on Wishes. Never the
 * size of the list (tab-badge). A child's bar carries no badges at all;
 * nothing in the product is ever urgent for a child.
 */

import { NavLink } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { useAttention } from '../lib/attention.js';
import { BankIcon, FamilyIcon, HomeIcon, StarIcon, SwitchIcon } from './Icons.jsx';
import './TabBar.css';

function Tab({ to, end, icon: Glyph, label, badge = 0 }) {
  return (
    <NavLink to={to} end={end} className="tabbar__tab">
      <span className="tabbar__glyph">
        <Glyph />
        {badge > 0 && (
          <span className="tabbar__badge">
            {badge > 9 ? '9+' : badge}
            <span className="visually-hidden"> waiting</span>
          </span>
        )}
      </span>
      <span className="tabbar__label">{label}</span>
    </NavLink>
  );
}

export default function TabBar() {
  const { family, profile } = useFamily();
  const attention = useAttention(family?.id, !profile);

  if (!family) return null;

  return (
    <nav className="tabbar" aria-label="Main">
      <div className="tabbar__inner">
        {profile ? (
          <>
            <Tab to="/" end icon={HomeIcon} label="Home" />
            <Tab to={`/bank/${profile.id}`} icon={BankIcon} label="My money" />
            <Tab to={`/wishes/${profile.id}`} icon={StarIcon} label="My list" />
            <Tab to="/switch" icon={SwitchIcon} label="Switch" />
          </>
        ) : (
          <>
            <Tab to="/" end icon={HomeIcon} label="Home" badge={attention.asks} />
            <Tab to="/bank" icon={BankIcon} label="Bank" />
            <Tab to="/wishes" icon={StarIcon} label="Wishes" badge={attention.readyWishes} />
            <Tab to="/family" icon={FamilyIcon} label="Family" />
          </>
        )}
      </div>
    </nav>
  );
}
